import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import ClimateChart from "./ClimateChart";
import ClimateInfoPanel from "./ClimateInfoPanel";
import HumidityCharts from "./HumidityCharts";
import SolarExposureCharts from "./SolarExposureCharts";
import { TabsContainer, Tab } from "./TabsContainer";
import { API_BASE_URL } from "../utils/config";

const DataView = () => {
  const location = useLocation();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [temperatureData, setTemperatureData] = useState(null);
  const [humidityData, setHumidityData] = useState(null);
  const [dewPointData, setDewPointData] = useState(null);
  const [wetBulbData, setWetBulbData] = useState(null);
  const [cloudCoverData, setCloudCoverData] = useState(null);
  const [solarRadiationData, setSolarRadiationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize with location from navigation state
  useEffect(() => {
    if (location.state?.selectedLocation) {
      setSelectedLocation(location.state.selectedLocation);
    }
  }, [location.state]);

  // Fetch climate data when a location is selected
  useEffect(() => {
    if (selectedLocation) {
      fetchClimateData(selectedLocation.url, "temperature");
      fetchClimateData(selectedLocation.url, "humidity");
      fetchClimateData(selectedLocation.url, "dewpoint");
      fetchClimateData(selectedLocation.url, "wetbulb");
      fetchClimateData(selectedLocation.url, "cloudcover");
      fetchClimateData(selectedLocation.url, "solarradiation");
    }
  }, [selectedLocation]);

  // Fetch climate data for a specific location
  const fetchClimateData = async (url, variable) => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = `${API_BASE_URL}/api/climate-data?url=${encodeURIComponent(
        url
      )}&variable=${variable}`;

      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();

      if (variable === "temperature") {
        setTemperatureData(data);
      } else if (variable === "humidity") {
        setHumidityData(data);
      } else if (variable === "dewpoint") {
        setDewPointData(data);
      } else if (variable === "wetbulb") {
        setWetBulbData(data);
      } else if (variable === "cloudcover") {
        setCloudCoverData(data);
      } else if (variable === "solarradiation") {
        setSolarRadiationData(data);
      }
    } catch (err) {
      if (variable === "temperature" || variable === "humidity") {
        setError(`Failed to fetch ${variable} data: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Combine humidity data with dew point and wet bulb data
  const combinedHumidityData = humidityData
    ? {
        ...humidityData,
        dewPoint: dewPointData,
        wetBulb: wetBulbData,
      }
    : null;

  return (
    <div className="w-full min-h-screen flex flex-col m-0 p-0 overflow-hidden">
      <header className="w-full flex justify-between items-center px-8 py-5 bg-white shadow-sm">
        <Link to="/" className="text-2xl font-bold text-gray-800 no-underline">
          CLIMA
        </Link>
        <nav className="flex gap-6">
          <Link
            to="/"
            className="text-gray-700 font-medium no-underline hover:text-gray-900"
          >
            Home
          </Link>
          <Link
            to="/about"
            className="text-gray-700 font-medium no-underline hover:text-gray-900"
          >
            About
          </Link>
          <Link
            to="/contact"
            className="text-gray-700 font-medium no-underline hover:text-gray-900"
          >
            Contact
          </Link>
        </nav>
      </header>

      <main className="w-full p-6 flex-grow bg-gray-50">
        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md mb-5">
            {error}
          </div>
        )}

        {!selectedLocation ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              No Location Selected
            </h2>
            <p className="text-gray-600 mb-4">
              Please select a location from the homepage to view climate data.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Return to Homepage
            </Link>
          </div>
        ) : (
          <>
            <section className="mb-8">
              <h1 className="text-3xl font-bold text-gray-800 mb-4">
                {selectedLocation.title}
              </h1>
            </section>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="text-gray-600">Loading data...</div>
              </div>
            ) : (
              <>
                <section className="mb-8 bg-white rounded-lg shadow-sm p-6">
                  <ClimateInfoPanel
                    temperatureData={temperatureData}
                    humidityData={humidityData}
                    selectedLocation={selectedLocation}
                  />
                </section>

                <section className="bg-white rounded-lg shadow-sm p-6">
                  <TabsContainer>
                    <Tab label="Temperature & Humidity">
                      <div className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                          Temperature Data
                        </h2>
                        {temperatureData ? (
                          <ClimateChart
                            title="Annual Temperature Profile"
                            data={temperatureData}
                            dataKey="temperature"
                          />
                        ) : (
                          <div className="text-gray-600">
                            Temperature data not available
                          </div>
                        )}
                      </div>

                      <div className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                          Humidity Data
                        </h2>
                        {humidityData ? (
                          <>
                            <ClimateChart
                              title="Annual Humidity Profile"
                              data={humidityData}
                              dataKey="humidity"
                            />
                            {combinedHumidityData && (
                              <HumidityCharts
                                humidityData={combinedHumidityData}
                              />
                            )}
                          </>
                        ) : (
                          <div className="text-gray-600">
                            Humidity data not available
                          </div>
                        )}
                      </div>
                    </Tab>

                    <Tab label="Solar & Light Exposure">
                      <div className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                          Solar & Light Exposure Data
                        </h2>
                        {cloudCoverData ? (
                          <SolarExposureCharts
                            solarData={cloudCoverData}
                            radiationData={solarRadiationData}
                          />
                        ) : (
                          <div className="text-gray-600">
                            Solar and light exposure data are not available for
                            this location.
                          </div>
                        )}
                      </div>
                    </Tab>
                  </TabsContainer>
                </section>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default DataView;
